// ウェルカムスペースの「ご挙式日」を、空いている日だけ選べるカレンダーにする。
//
// セットは1色につき1つしかないため、1件の予約が挙式日の前後の日程まで占有する。
// 挙式日 9/21 の実際の流れ:
//   9/17 発送 → 9/19〜22 お客様の手元 → 9/23 戻り・検品 → 9/24 次を発送できる
// つまりセットが手を離れているのは 9/17〜9/23 の 7 日間（X-4 〜 X+2）。
//
//   予約済み挙式日 B と新規挙式日 X はどちらも同じ 7 日間を占有するので、
//   ぶつからないためには 7 日以上離れている必要がある = |X - B| <= 6 なら選べない。
//   B の前後 6 日を塞ぐ。「お客様の手元にある期間」だけを塞ぐと、
//   発送日や検品日が前の予約と重なる日程が通ってしまう。
//
// 予約済みの挙式日は商品メタフィールド custom.booked_wedding_dates（日付リスト）から渡ってくる。

export const SHIP_BEFORE_DAYS = 4; // 挙式日の何日前に発送するか（9/21 の挙式なら 9/17）
export const NEXT_SHIP_AFTER_DAYS = 3; // 挙式日の何日後から次を発送できるか（9/21 の挙式なら 9/24）

// お客様の手元にある期間（3泊4日）。挙式日を選ぶとこの範囲をカレンダー上に出す
export const DELIVER_BEFORE_DAYS = 2; // 挙式日の何日前にお届けするか
export const RETURN_AFTER_DAYS = 1; // 挙式日の何日後にご返送いただくか

const DAY = 24 * 60 * 60 * 1000;

// "YYYY-MM-DD" をローカル時刻の Date にする。new Date("2026-09-21") は UTC 解釈なので、
// JST では前日になってしまう。日本時間の暦日として扱いたいので自前で分解する。
export function parseDate(value) {
  if (value instanceof Date) return isNaN(value) ? null : value;
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d) ? null : d;
}

// 同じ理由で toISOString() は使わない（JST の午前9時前は前日の日付になる）。
export function toISO(date) {
  const p = (n) => String(n).padStart(2, '0');
  return date.getFullYear() + '-' + p(date.getMonth() + 1) + '-' + p(date.getDate());
}

const shift = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

// 占有期間が 7 日なので、挙式日は 7 日以上離れていなければならない = 前後 6 日がブロック
export function blockSpan(shipBefore = SHIP_BEFORE_DAYS, nextShipAfter = NEXT_SHIP_AFTER_DAYS) {
  return shipBefore + nextShipAfter - 1;
}

// 予約済み挙式日の配列 → 選択不可の区間（flatpickr の disable にそのまま渡せる形）
export function blockedRanges(bookedDates, shipBefore = SHIP_BEFORE_DAYS, nextShipAfter = NEXT_SHIP_AFTER_DAYS) {
  const span = blockSpan(shipBefore, nextShipAfter);
  return (bookedDates || [])
    .map(parseDate)
    .filter(Boolean)
    .map((b) => ({ from: shift(b, -span), to: shift(b, span) }));
}

export function isBlocked(date, bookedDates, shipBefore = SHIP_BEFORE_DAYS, nextShipAfter = NEXT_SHIP_AFTER_DAYS) {
  const d = parseDate(date);
  if (!d) return false;
  const span = blockSpan(shipBefore, nextShipAfter);
  return (bookedDates || []).map(parseDate).filter(Boolean).some((b) => Math.abs(d - b) <= span * DAY);
}

// 選んだ挙式日に対して、お客様の手元にある期間
export function rentalWindow(weddingDate, before = DELIVER_BEFORE_DAYS, after = RETURN_AFTER_DAYS) {
  const d = parseDate(weddingDate);
  if (!d) return null;
  return { deliver: shift(d, -before), wedding: d, send_back: shift(d, after), nights: before + after };
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
export function formatDay(date) {
  return date.getMonth() + 1 + '月' + date.getDate() + '日(' + WEEKDAYS[date.getDay()] + ')';
}

// 最短の挙式日。発送は挙式日の SHIP_BEFORE_DAYS 前なので、その発送日が明日以降になる日から。
// 当日発送できるなら +1 を外す。
export function earliestDate(today = new Date(), shipBefore = SHIP_BEFORE_DAYS) {
  return shift(parseDate(toISO(today)), shipBefore + 1);
}

// ---- ここから下は DOM 側。テストからは import されない ----

function readBooked() {
  const el = document.getElementById('kb-ws-booked-dates');
  if (!el) return [];
  try {
    const parsed = JSON.parse(el.textContent || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return []; // 壊れていても購入自体は止めない（空き確認は注文後の連絡で担保されている）
  }
}

// カレンダーを閉じるボタン。日付を選ぶまでは押せない
function buildConfirmButton(fp) {
  const btn = document.createElement('button');
  btn.type = 'button'; // 購入フォームの中にあるので、submit にしてはいけない
  btn.className = 'kb-ws-confirm';
  btn.textContent = '決定';
  btn.disabled = !fp.selectedDates.length;
  btn.addEventListener('click', () => fp.close());
  return btn;
}

// サマリーのアイコン（線画）。装飾なので読み上げ対象から外す
const SVG_OPEN =
  '<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
const ICON_PACKAGE =
  SVG_OPEN + '<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z"/><path d="M12 12l8-4.5"/><path d="M12 12v9"/><path d="M12 12L4 7.5"/></svg>';
const ICON_BELL =
  SVG_OPEN + '<path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3H4a4 4 0 0 0 2-3v-3a7 7 0 0 1 4-6"/><path d="M9 17v1a3 3 0 0 0 6 0v-1"/></svg>';
const ICON_PENCIL =
  SVG_OPEN + '<path d="M4 20h4L18.5 9.5a2.83 2.83 0 0 0-4-4L4 16v4"/><path d="M13.5 6.5l4 4"/></svg>';

// 入力欄の下に「お届け → ご挙式 → ご返送」を出す
function renderSchedule(input, weddingDate) {
  let box = input.nextElementSibling;
  if (!box || !box.classList.contains('kb-ws-schedule')) {
    box = document.createElement('div');
    box.className = 'kb-ws-schedule';
    input.insertAdjacentElement('afterend', box);
  }
  const w = rentalWindow(weddingDate);
  if (!w) {
    box.hidden = true;
    box.textContent = '';
    return;
  }
  box.hidden = false;
  const steps = [
    { label: 'お届け', date: w.deliver, icon: ICON_PACKAGE },
    { label: 'ご挙式', date: w.wedding, icon: ICON_BELL, wedding: true },
    { label: 'ご返送手続き', date: w.send_back, icon: ICON_PENCIL },
  ];
  box.innerHTML = steps
    .map(
      (s) =>
        '<div class="kb-ws-step' + (s.wedding ? ' is-wedding' : '') + '">' +
        s.icon +
        '<span class="lbl">' + s.label + '</span>' +
        '<span class="val">' + formatDay(s.date) + '</span>' +
        '</div>'
    )
    .join('<span class="kb-ws-arrow" aria-hidden="true"></span>');
}

function init() {
  const booked = readBooked();
  const min = earliestDate();
  const ranges = blockedRanges(booked);

  document.querySelectorAll('input[name="properties[ご挙式日]"]').forEach(function (el) {
    if (el.dataset.kbBooking === 'ready') return;
    el.dataset.kbBooking = 'ready';
    el.removeAttribute('maxlength');

    if (window.flatpickr) {
      window.flatpickr.localize(window.flatpickr.l10ns.ja);
      window.flatpickr(el, {
        locale: 'ja',
        dateFormat: 'Y-m-d',
        minDate: min,
        disable: ranges,
        disableMobile: true, // OS のカレンダーだと埋まっている日を出し分けられない
        // 1回目は1タップで確定させる。閉じたあとは開いたままに切り替えて、
        // 選び直すときに前後のレンタル期間を見比べられるようにする（onClose 参照）
        closeOnSelect: true,
        onReady: (_s, _v, fp) => {
          fp.calendarContainer.classList.add('kb-ws-calendar');
          fp.calendarContainer.appendChild(buildConfirmButton(fp));
          renderSchedule(el, fp.selectedDates[0]);
        },
        // 選んだ挙式日の前後（お届け〜ご返送）をカレンダー上で1本の帯として塗る
        onDayCreate: (_s, _v, fp, dayElem) => {
          const sel = fp.selectedDates[0];
          if (!sel) return;
          const w = rentalWindow(sel);
          const d = dayElem.dateObj;
          if (d < w.deliver || d > w.send_back) return;
          dayElem.classList.add('kb-hold');
          // 帯の端だけ角を丸める。週の折り返しでも切れ目を丸めておく
          if (+d === +w.deliver || d.getDay() === 0) dayElem.classList.add('kb-cap-l');
          if (+d === +w.send_back || d.getDay() === 6) dayElem.classList.add('kb-cap-r');
        },
        onClose: (_s, _v, fp) => {
          if (fp.selectedDates.length) fp.config.closeOnSelect = false;
        },
        onChange: (_s, _v, fp) => {
          fp.redraw(); // onDayCreate をもう一度走らせて塗り直す
          renderSchedule(el, fp.selectedDates[0]);
          const btn = fp.calendarContainer.querySelector('.kb-ws-confirm');
          if (btn) btn.disabled = !fp.selectedDates.length;
        },
      });
      return;
    }

    // flatpickr が読めなかったときの保険。ネイティブの日付入力に落とし、
    // 埋まっている日を選ばれたら送信前に止める（カレンダー上でのグレーアウトはできない）。
    el.type = 'date';
    el.min = toISO(min);
    el.addEventListener('change', function () {
      const ng = isBlocked(el.value, booked);
      el.setCustomValidity(ng ? 'この日程は他のご予約と重なるためお選びいただけません。' : '');
      if (ng) el.reportValidity();
      renderSchedule(el, ng ? null : el.value);
    });
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // テーマエディタでの再描画、およびボトムシートへの購入フォーム移動に追随する
  document.addEventListener('shopify:section:load', init);
}
